import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { AspectTag, MediaItem, Region } from "@gooner/shared";
import {
  aspectPenalty,
  aspectTag,
  autoOptimizeActive,
  FULL_IMAGE_SECONDS,
  isOwnMediaUrl,
  capRung,
  pickRenditionHeight,
  regionPhotoSeconds,
  rungUrl,
  tileSourceHeight,
} from "@gooner/shared";
import { api, ApiHttpError } from "../lib/api";
import { mediaErrorMessage, track, wouldSend } from "../analytics";
import { isSafeMode } from "../lib/safe-mode";
import type { SyncBus } from "./syncBus";
import { shuffle } from "../lib/shuffle";
import { watchBegin, watchEnd } from "../lib/watchReport";
import { warmMedia } from "./mediaWarm";
import { budgetFor, deviceDpr } from "./playbackBudget";
import { isParked, park, unpark } from "./decoderPark";
import { useOnscreenRef, useTileOnscreen } from "./useTileOnscreen";
import { focalZoomCeiling, useAdaptiveFraming } from "./useAdaptiveFraming";
import type { RegionController, RegionRegistry } from "./types";

/**
 * Why a <video> failed, as a number the event can carry.
 *
 * MediaError code 4 (SRC_NOT_SUPPORTED) is what a browser reports for a codec it cannot
 * play AND for a response it was not allowed to read — a 403 once the age cookie or the
 * url signature has lapsed. On its own it cannot say whether a wall going quietly heavier
 * is a transcode bug or an expiry, and in a month of production data it was 98% of the
 * errors. One byte from the same url tells them apart: 200/206 means the bytes were fine
 * and the codec was not; 403 is the token; null is the network.
 *
 * Only ever run for an event the limiter will actually send (see the caller), and capped
 * so a lapsed wall is at most five tiny requests a minute, not one per failing tile.
 */
async function probeMediaStatus(url: string): Promise<number | null> {
  try {
    const r = await fetch(url, {
      headers: { range: "bytes=0-0" },
      cache: "no-store",
      signal: AbortSignal.timeout(3000),
    });
    return r.status;
  } catch {
    return null;
  }
}

/** How many items before the end of the buffer we proactively fetch the next page. */
const PREFETCH_AHEAD = 3;
/** Crossfade duration; kept in sync with the inline transition style below. */
const FADE_MS = 600;
/** How long a re-frame takes. Matches the harness the recipe was validated against. */
const FRAMING_MOVE_MS = 700;
/** Readiness gate: hold the current clip on screen until the incoming one can
 *  actually play, so a swap never reveals a black/poster frame. Safety cap so a
 *  broken / never-buffering clip can't freeze the tile forever. */
const REVEAL_TIMEOUT_MS = 10_000;
/** How far a clip must really have played before it is a view. On the media clock, so a
 *  stall, a swipe past or a file that will not open never reaches it. */
const VIEW_MIN_PLAY_SEC = 1;
/** Default backoff before retrying a failed fetch (overridden by a server retry-after). */
const DEFAULT_BACKOFF_MS = 30_000;
/** Videos advance on `ended`; this is only a safety net so a stalled video that never
 *  fires it eventually moves on. A clip that never became PLAYABLE does not get this
 *  long — see `stuckRef`: it was revealed on its poster and is treated as a still. */
const FULL_VIDEO_SAFETY_MS = 10 * 60_000;
/**
 * How long the VISIBLE video may sit without making progress before the tile moves on.
 *
 * Between the readiness gate (a clip is only revealed once it can play) and `ended`
 * (which is the advance), nothing watched the middle: a clip that started, then starved
 * — the buffer ran dry and `waiting` fired — sat on its last frame until the ten-minute
 * net above. Seen on Maximum Dopamine: sixteen unsynced tiles of 0–3s clips is more
 * concurrent fetch and decode than a cheap display has, and the first tile to starve
 * held its two decoders for ten minutes, which starved the next. Twelve seconds is four
 * times the longest clip on that wall and well past any rebuffer that is going to end.
 */
const STALL_ADVANCE_MS = 12_000;

/** Stable identity for a source so we can detect real changes (not object churn). */
function sourceKey(r: Region): string {
  return JSON.stringify(r.source);
}

/**
 * Recently-shown clips per DISPLAY, keyed by the registry every region of a view already
 * shares. `isVisibleElsewhere` only knows "on screen at this instant", which is why a
 * clip could leave one tile and reappear in a sibling seconds later — the wall had no
 * memory. This is that memory: id -> last reveal time, honored by nextDistinctIndex as a
 * SOFT preference (a pool smaller than the window still plays; see the two-tier scan).
 */
const recentShown = new WeakMap<RegionRegistry, Map<string, number>>();
/** How long a clip that just played anywhere on this display is dispreferred. */
const RECENT_MS = 3 * 60_000;
/** Hard cap on remembered ids, pruned oldest-first — a wall consumes ~4/min per tile. */
const RECENT_CAP = 400;
function recentOf(registry: RegionRegistry): Map<string, number> {
  let m = recentShown.get(registry);
  if (!m) {
    m = new Map();
    recentShown.set(registry, m);
  }
  return m;
}
function markRecent(registry: RegionRegistry, id: string): void {
  const m = recentOf(registry);
  m.delete(id); // re-insert at the tail so Map order stays oldest-first
  m.set(id, Date.now());
  if (m.size > RECENT_CAP) {
    for (const k of m.keys()) {
      if (m.size <= RECENT_CAP) break;
      m.delete(k);
    }
  }
}

/** The url a slot will actually load for an item — the rendition for a video, the
 *  plain url for a still. One answer, so prefetching and playing can't disagree about
 *  what to ask for and turn one download into two. */
function mediaSrc(item: MediaItem, renditionHeight: number | null): string {
  return item.kind === "video" ? rungUrl(item.url, renditionHeight) : item.url;
}

/**
 * Whether a media element should load this url as a CORS request.
 *
 * Region sync is what makes this matter. A follower does not fetch the clip; it takes the
 * LEADER's decoded frames via `captureStream()` (SyncedRegion). An element loaded
 * cross-origin WITHOUT CORS is tainted, and `captureStream()` on a tainted element gives
 * the follower a stream it cannot render — a black tile that nothing throws on and that
 * reads as a sync bug rather than a CORS one.
 *
 * Applied ONLY to urls this app serves. Not everything here is ours: a clip whose cache
 * entry was evicted falls back to its pre-cache origin (`origin_url`), so reddit and imgur
 * urls reach these elements directly. Asking for CORS on those would turn a working
 * playback into a failed load wherever the upstream omits `access-control-allow-origin` —
 * trading a limitation those clips already have (they cannot lead a synced region) for a
 * clip that does not play at all.
 *
 * Same-origin today, where the attribute costs nothing: no CORS check applies to a
 * same-origin request. It earns its keep the moment media moves to another hostname,
 * which is precisely when the failure would be hardest to attribute.
 */
function corsModeFor(url: string): "anonymous" | undefined {
  return isOwnMediaUrl(url) ? "anonymous" : undefined;
}

interface RegionPlayerProps {
  region: Region;
  registry: RegionRegistry;
  /** Where this region announces what it is playing, for any regions synced to it.
   *  Absent on surfaces with no synced regions (cards, previews). */
  syncBus?: SyncBus;
  /** Global paused state pushed from the DisplayApp (pause/resume commands). */
  paused: boolean;
  /**
   * Whether this tile is to be SILENT. The caller computes it — the screen's master
   * mute OR'd with the region's own `soundOn` (the room decides whether there is
   * sound at all; the layout decides WHICH panel carries it — see Region.soundOn).
   * This player just obeys the verdict.
   */
  muted: boolean;
  /** The region's loudness, 0..1, applied while this tile is audible. The caller reads
   *  it off the region through `regionVolume` (absence = the half default). */
  volume?: number;
  /** The browser refused to play with sound and this tile fell back to muted — so the
   *  screen can show what is actually happening rather than what was asked for. */
  onAutoMuted?: () => void;
  /** Called each time a clip appears on screen (used to tally session views). */
  onView?: (item: MediaItem) => void;
  /**
   * The clips riding on `region.source` came out of `/source/fetch` moments ago — the
   * Infinity stage deals its pages from one — so a `clip` / `clips` source may be played
   * straight off the region without asking the server to echo it back (see fetchMore).
   *
   * OFF by default, and that default is the ban: `/source/fetch` strips banned clips
   * from every result, and for a clip locked into a saved view that filter is the ONLY
   * thing between a banned clip and the glass. A host that cannot vouch for where the
   * region's clips came from must not set this.
   */
  vetted?: boolean;
}

interface Slot {
  /** Monotonic key so React fully remounts the media element per item. */
  key: number;
  item: MediaItem | null;
  visible: boolean;
  /**
   * Attach this item's poster image — set when a still beats a black tile.
   *
   * Off by default because on a wall a poster is a picture nobody ever sees. The
   * readiness gate holds the PREVIOUS clip on screen until the incoming one can play,
   * which is the same job a poster does and does it better — with real motion instead of
   * a still. The browser fetches a poster eagerly all the same, and 45% of the catalog
   * carries one at ~98 KB, which measured as 7% of a running wall's traffic spent on
   * frames that never reached a screen.
   *
   * Two exceptions pay for the request: the stuck-reveal timeout (a clip that never
   * buffers), and a BARE first paint (nothing on screen yet — see `show`), where
   * waiting on the warm cache would leave a black spinner.
   */
  poster?: boolean;
}

export function RegionPlayer({
  region,
  registry,
  paused,
  muted,
  volume = 1,
  onAutoMuted,
  onView,
  syncBus,
  vetted = false,
}: RegionPlayerProps) {
  const fit = region.fit === "contain" ? "object-contain" : "object-cover";
  const onAutoMutedRef = useRef(onAutoMuted);
  onAutoMutedRef.current = onAutoMuted;
  // Keep the latest onView reachable from show() without rebuilding the callback.
  const onViewRef = useRef(onView);
  onViewRef.current = onView;

  // Per-user WATCH SECONDS, batched (lib/watchReport). Gated on THIS being a real,
  // countable surface exactly as the view count is — a slot is opened only where
  // onView fires (below, at reveal), which a synced follower and every preview omit.
  // A stable slot id per mounted region, so the batcher can close the previous
  // interval when the clip changes and bank the last one when the tile unmounts.
  const watchSlot = useId();
  useEffect(() => () => watchEnd(watchSlot), [watchSlot]);
  /** The slot whose first view has been counted, and the slot with an interval open. */
  const countedKeyRef = useRef<number>(-1);
  const watchKeyRef = useRef<number>(-1);

  // Two stacked layers we crossfade between.
  const [front, setFront] = useState<Slot>({ key: 0, item: null, visible: true });
  const [back, setBack] = useState<Slot>({ key: 1, item: null, visible: false });
  const frontIsTop = useRef(true);
  const slotSeq = useRef(2);
  // Key of the slot currently driving playback. Used so only the *visible* clip
  // advances on `ended` — the outgoing clip in a crossfade must not double-advance.
  const visibleKeyRef = useRef<number>(-1);
  // The item actually ON SCREEN — written only by reveal(). `indexRef` advances the
  // moment a swap STARTS, so it names the incoming clip for the whole warm window;
  // reporting that as "current" made the still-visible outgoing clip invisible to
  // sibling regions, which promptly re-picked it (the cross-region ping-pong).
  const visibleItemRef = useRef<MediaItem | null>(null);
  // A slot rendered into the hidden layer but NOT yet revealed: we keep the
  // current clip on screen until this one can play (readiness gate). Null = idle.
  const pendingRevealRef = useRef<{
    key: number;
    item: MediaItem;
    into: "front" | "back";
    /** False while the clip's bytes are still being fetched — see `show`. Nothing may
     *  reveal or play a slot that has not been rendered yet. */
    mounted: boolean;
  } | null>(null);

  const [status, setStatus] = useState<"loading" | "ready" | "empty" | "error">("loading");
  // This tile's rendered size in REAL pixels (CSS px × devicePixelRatio). Null until
  // measured, which reads as "no rendition yet" and serves the master — the same thing
  // the height-only version did before its first measurement.
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Last-resort net: if a rendition url fails to load at all, latch this region back to
  // the full master — better a heavier clip than a permanently black tile. It should
  // almost never fire now, because a rung that isn't built yet redirects to the master
  // rather than 404ing (routes/media.ts); what's left is a clip whose cache entry is
  // gone, where the master would fail too.
  const [renditionsOk, setRenditionsOk] = useState(true);

  // The switch AND nothing overriding it: a source that names `quality:*` or `aspect:*`
  // has already settled the axis this would settle, and the hand beats the guess.
  const auto = autoOptimizeActive(region);
  /**
   * The orientation this region prefers, or null for "no preference" — which covers a
   * roughly square tile, a tile not yet measured, and auto-optimise being off. One value
   * for all three, so every reader below gets the opt-out for free.
   */
  const shape: AspectTag | null = auto && box ? aspectTag(box.w, box.h) : null;

  /**
   * Everything the fit rules read, in a ref.
   *
   * `show`, `reveal` and the advance scan are built once per source and run from timers
   * and media-element handlers; closing over `box` or the toggle would freeze whichever
   * value was current when the source last changed. Same reason `suspended()` goes
   * through refs — see the note there.
   */
  // This display's decode budget (one per registry) and this tile's grant from it. A tile
  // without a grant is suspended like an off-screen one: it holds its frame (or its poster,
  // if it never had one) until a decode slot frees. Always granted unless the budget has
  // had to limit how many tiles decode at once.
  const budget = budgetFor(registry);
  const budgetRef = useRef(budget);
  budgetRef.current = budget;
  const grantRef = useRef(budget.granted(region.id));
  const [granted, setGranted] = useState(grantRef.current);

  const fitRef = useRef({
    box: null as { w: number; h: number } | null,
    shape: null as AspectTag | null,
    auto: true,
    fit: region.fit,
    renditionsOk: true,
    region,
  });
  fitRef.current = { box, shape, auto, fit: region.fit, renditionsOk, region };

  /**
   * The ladder rung to fetch this clip at, or null for the full master.
   *
   * Per CLIP, not per region: two clips of different shapes in the same box need
   * different amounts of source (see `tileSourceHeight`), and every caller that builds a
   * url — the element, the read-ahead warm, the sync announcement — goes through here so
   * they cannot disagree and turn one download into two.
   *
   * Null, so the master, in three cases: the tile is unmeasured, a rendition already
   * failed to load (the latch), or auto-optimise is off. That last one is the toggle's
   * whole quality half — off means take the best bytes there are and stop guessing.
   */
  const rungFor = useCallback((item: MediaItem): number | null => {
    // An upload has no ladder — it is its master, full stop (media-store.ts). Asking for
    // a rung anyway cost a 302 per play, and the redirect target is a differently-signed
    // master url than the one the warm cached, so every upload downloaded twice.
    if (item.sourceKind === "upload") return null;
    const f = fitRef.current;
    if (!f.auto || !f.renditionsOk || !f.box) return null;
    // Adaptive Framing may zoom into the crop, and a rung sized for the un-zoomed tile
    // is then being blown up — visibly soft, and indistinguishable from the encoder
    // having got worse. Ask for the source the tightest shot will actually need.
    const zoom = focalZoomCeiling(item, f.region, f.box.h > 0 ? f.box.w / f.box.h : 0);
    // Under the device's ceiling: the phone setting, the decode probe and the live budget
    // (playbackBudget.ts). Read per call, so a lowered ceiling reaches the next clip.
    return capRung(
      pickRenditionHeight(Math.round(tileSourceHeight(f.box, item, f.fit) * zoom)),
      budgetRef.current.ceiling(),
    );
  }, []);

  // --- Refs holding the live playback buffer (avoids stale closures in timers). ---
  const itemsRef = useRef<MediaItem[]>([]);
  const indexRef = useRef(0);
  const cursorRef = useRef<string | undefined>(undefined);
  const fetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pausedRef = useRef(paused);
  const mountedRef = useRef(true);

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /**
   * The visible clip is on screen WITHOUT ever having become playable — the reveal
   * timeout gave up on it and showed its poster (or its error left nothing to show).
   *
   * While this is set, `arm` treats the tile as a still: FULL_IMAGE_SECONDS, not the
   * ten-minute video net. That net assumes a clip that is PLAYING and will fire `ended`;
   * a clip that never buffered fires nothing, and on Maximum Dopamine the difference was
   * a tile frozen on a thumbnail for ten minutes while its two decoders stayed held.
   * Cleared when a new clip is queued, and if the stuck element does come good after
   * all (its `canplay` fires late), so a slow clip that recovers gets to play out.
   */
  const stuckRef = useRef(false);
  /** The stall watchdog for the VISIBLE slot — see STALL_ADVANCE_MS. */
  const stallTimer = useRef<{ key: number; t: ReturnType<typeof setTimeout> } | undefined>(undefined);
  /** `arm`, reachable from `show`, which is built before it. Assigned once `arm` exists. */
  const armRef = useRef<(stagger?: boolean, since?: number) => void>(() => undefined);
  /**
   * This region's photo hold (Region.photoSeconds, the wall's Photo time chip), in a ref
   * for the same reason as everything else `arm` reads: it runs from timers built once
   * per source. `photoSinceRef` is when the photo hold now armed STARTED — null while
   * the armed timer is anything else (a video's net, a stuck clip's fixed 6s, nothing) —
   * so a slider move can re-time the photo already on screen instead of restarting it.
   */
  const photoMsRef = useRef(regionPhotoSeconds(region) * 1000);
  const photoSinceRef = useRef<number | null>(null);
  /**
   * How much of the photo hold had run (ms) when the decode budget took this tile's grant.
   * Slots are time-sliced (SLOT_SLICE_MS, 8s) and a held tile runs no timer, so a hold that
   * restarted on every turn could never finish if it was longer than one slice: a 10s photo
   * froze for as long as the budget stayed limited. The next turn carries on from here.
   */
  const photoServedRef = useRef<number | null>(null);
  // How long to wait before the next retry; raised on rate-limit responses.
  const retryDelayRef = useRef(DEFAULT_BACKOFF_MS);

  // Keep refs to the actual <video> elements so commands can pause/resume them.
  const frontVideo = useRef<HTMLVideoElement | null>(null);
  const backVideo = useRef<HTMLVideoElement | null>(null);

  /*
   * Sound follows the SCREEN's mute state, applied to the live elements directly.
   *
   * The `muted` attribute on the element below seeds a fresh element, but a toggle has to
   * reach the two that are already playing, and React's handling of `muted` on updates is
   * the kind of thing nobody should build a remote's button on. So: set the property.
   *
   * Unmuting may be REFUSED. A browser will not let a page nobody has touched start making
   * noise, and the remote's unmute is exactly that — a command arriving over a socket, with
   * no gesture on this document behind it. If the element is sitting paused after the
   * flip, `play()` asks again; if that is refused, the tile goes back to muted rather than
   * silently sitting there, and the screen is TOLD, so its button and the remote's show
   * what actually happened instead of what was asked for.
   *
   * Only the layer on screen, and only a live tile, gets that second ask. The flip reaches
   * every element, and without this an unmute set an off-screen page or a faded-out layer
   * playing, out loud, with nothing left to stop it.
   */
  useEffect(() => {
    let refused = false;
    const onTop = frontIsTop.current ? frontVideo.current : backVideo.current;
    for (const v of [frontVideo.current, backVideo.current]) {
      if (!v) continue;
      v.muted = muted;
      if (muted || !v.paused || v !== onTop || suspended()) continue;
      v.play().catch(() => {
        v.muted = true;
        refused = true;
        v.play().catch(() => undefined);
      });
    }
    // Reported after the loop rather than inside it, so one refusal is one report
    // even when both slots are live mid-crossfade.
    if (refused) onAutoMutedRef.current?.();
  }, [muted]);

  /*
   * The region's loudness, delivered the same two-pronged way as `muted` above: this
   * effect reaches the pair of elements already playing, and the slot's callback ref
   * (see renderSlot) seeds a fresh element the moment it mounts — React has no `volume`
   * attribute to put on the tag, so without the ref-time write every crossfade would
   * arrive at full volume for its first beat. Both slots on purpose: mid-crossfade both
   * are audible, and a fade that jumped levels for its last 600ms would be heard.
   *
   * iOS ignores HTMLMediaElement.volume (read-only there); the on/off half still works
   * through `muted`, which is the half that matters on a phone speaker.
   */
  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  useEffect(() => {
    for (const v of [frontVideo.current, backVideo.current]) {
      if (v) v.volume = volume;
    }
  }, [volume]);

  // Read by `reveal`, which is built once: taking these as dependencies would rebuild it
  // mid-crossfade, which is the bug the rest of this file is carefully avoiding.
  const syncBusRef = useRef(syncBus);
  syncBusRef.current = syncBus;

  pausedRef.current = paused;

  // Off screen — page hidden, or this tile scrolled out of view — is a second, quite
  // separate reason not to be playing. Kept apart from `paused` rather than folded into
  // it because they are answers to different questions: `paused` is what the wall was
  // TOLD to do, and resuming must not restart a tile nobody can see.
  const onscreen = useTileOnscreen(containerRef);
  const onscreenRef = useOnscreenRef(onscreen);
  /** Should this tile be still? Read from timers and handlers built once per source,
   *  so it has to go through refs rather than closed-over values. */
  const suspended = useCallback(
    () => pausedRef.current || !onscreenRef.current || !grantRef.current,
    [onscreenRef],
  );

  /*
   * A CLIP COUNTS WHEN IT IS WATCHED, NOT WHEN IT IS LOADED (2026-10-31).
   *
   * `reveal` is about the tile, not about anybody looking at it. Infinite Scroll mounts the
   * page either side of the glass, and a tile with nothing on it reveals the moment it
   * mounts — so the page BELOW you was counted as a view before you ever reached it, and
   * its watch interval opened. The interval then ran until the page unmounted, which put
   * the page ABOVE you on the clock for as long as you stayed on the one you came for:
   * the clip before the one you watched the most came out ahead of it.
   *
   * So this is the one gate. An interval is open only while the tile is live (on screen,
   * unpaused, holding a decode grant). A video's first view waits for the media clock
   * (onTimeUpdate, VIEW_MIN_PLAY_SEC); a still has no clock, so being live is its view.
   * onView present is the discriminator as ever: followers and previews report nothing.
   * A loop is one interval with its seconds accruing, not a new interval per repeat.
   */
  const syncWatch = useCallback(() => {
    if (!onViewRef.current) return;
    const item = visibleItemRef.current;
    if (!item || suspended()) {
      watchEnd(watchSlot);
      watchKeyRef.current = -1;
      return;
    }
    const key = visibleKeyRef.current;
    if (watchKeyRef.current !== key) {
      watchBegin(watchSlot, item);
      watchKeyRef.current = key;
    }
    if (item.kind !== "video" && countedKeyRef.current !== key) {
      countedKeyRef.current = key;
      onViewRef.current(item);
    }
  }, [suspended, watchSlot]);

  // Measure this tile's rendered size (once mounted + on resize) to choose a
  // size-matched rendition and an orientation to prefer. Runs before the first clip
  // loads — which waits on a network fetch — so even the first src already uses the
  // right rendition.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      // Capped (#13): a DPR-3 phone measured raw asked for the 1920 rung. See deviceDpr.
      const dpr = deviceDpr().effective;
      const next = {
        w: Math.round(el.clientWidth * dpr),
        h: Math.round(el.clientHeight * dpr),
      };
      // Same-value bailout, by hand: this is an object now, so React cannot see that a
      // ResizeObserver callback reporting the identical box is a no-op, and a wall of
      // tiles re-rendering on every observer beat is not free.
      setBox((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A leader that unmounts — the layout changed, the wall was retargeted — must say so.
  // Left unsaid, its followers hold the last frame forever, which reads as a crash.
  useEffect(() => {
    const bus = syncBusRef.current;
    const id = region.id;
    return () => bus?.clear(id);
  }, [region.id]);

  const clearAdvance = useCallback(() => {
    photoSinceRef.current = null;
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = undefined;
    }
  }, []);

  /** Pull the next page of media and append; guarded against overlap. */
  const fetchMore = useCallback(async (): Promise<void> => {
    if (fetchingRef.current || !hasMoreRef.current) return;
    fetchingRef.current = true;
    try {
      // A locked clip (or a fixed handful) already rides on the region — ClipAdapter /
      // ClipsAdapter just echo it back. Skipping the round trip matters on Goonette of
      // the Hill: four challengers would otherwise each POST the whole MediaItem before
      // any tile can mount, stacking RTT on top of the warm wait that left them black.
      //
      // ONLY WHEN THE HOST VOUCHES FOR THEM (`vetted`). The echo is not a pure echo: the
      // route drops banned clips on the way back, and a clip locked into a saved view
      // has no other gate — skipped for everyone, a banned clip kept playing there.
      const src = region.source;
      if (vetted && (src.kind === "clip" || src.kind === "clips")) {
        const items = src.kind === "clip" ? (src.clip ? [src.clip] : []) : src.clips;
        if (!mountedRef.current) return;
        if (items.length > 0) {
          itemsRef.current = itemsRef.current.concat(items.length > 1 ? shuffle(items) : items);
        }
        hasMoreRef.current = false;
        if (itemsRef.current.length === 0) {
          setStatus((s) => (s === "error" ? s : "empty"));
        }
        return;
      }
      const res = await api.fetchSource(src, cursorRef.current);
      if (!mountedRef.current) return;
      if (res.items.length > 0) {
        // Shuffle each page per player so regions sharing a source don't play the
        // same clip in lockstep (each RegionPlayer walks its own random order).
        itemsRef.current = itemsRef.current.concat(shuffle(res.items));
      }
      cursorRef.current = res.nextCursor;
      hasMoreRef.current = Boolean(res.nextCursor);
      if (itemsRef.current.length === 0) {
        setStatus((s) => (s === "error" ? s : "empty"));
      }
    } catch (err) {
      if (!mountedRef.current) return;
      // Back off before retrying; honor the server's retry-after on rate limits,
      // and add jitter so multiple regions don't retry in lockstep.
      const retryAfter = err instanceof ApiHttpError ? err.retryAfterSec : undefined;
      const base = retryAfter && retryAfter > 0 ? retryAfter * 1000 : DEFAULT_BACKOFF_MS;
      retryDelayRef.current = base + Math.floor(Math.random() * 1500);
      // Only surface an error if we have nothing to show; otherwise keep playing.
      if (itemsRef.current.length === 0) setStatus("error");
    } finally {
      fetchingRef.current = false;
    }
  }, [region.source, vetted]);

  /** Crossfade a readied slot in and the current one out; count the view only now
   *  that it's actually on screen. No-op unless it's the slot we're waiting on. */
  const reveal = useCallback((key: number) => {
    const pend = pendingRevealRef.current;
    if (!pend || pend.key !== key) return;
    pendingRevealRef.current = null;
    if (revealTimer.current) {
      clearTimeout(revealTimer.current);
      revealTimer.current = undefined;
    }
    // The element we are about to fade OUT, captured as a node rather than as a ref:
    // by the time the fade ends the ref may point at a newer element, and pausing the
    // wrong one blacks out a live tile.
    const outgoing = pend.into === "back" ? frontVideo.current : backVideo.current;
    if (pend.into === "back") {
      setBack((b) => ({ ...b, visible: true }));
      setFront((f) => ({ ...f, visible: false }));
      frontIsTop.current = false;
    } else {
      setFront((f) => ({ ...f, visible: true }));
      setBack((b) => ({ ...b, visible: false }));
      frontIsTop.current = true;
    }
    visibleKeyRef.current = key;
    visibleItemRef.current = pend.item;
    // The display-wide recency memory: this clip is now dispreferred everywhere on the
    // wall for RECENT_MS, so it cannot hop straight into a sibling tile.
    markRecent(registry, pend.item.id);

    /*
     * Stop the faded-out layer decoding.
     *
     * Both layers stay mounted so the crossfade has something to fade between, and the
     * outgoing one keeps replaying (onEnded restarts it as the gap bridge) — so without
     * this it plays on forever at opacity 0. Measured on a 3x4 wall: 24 videos decoding
     * for 12 tiles, exactly double, and concurrent decode on the display is the wall's
     * known bottleneck (docs/perf/decode-load.md).
     *
     * After FADE_MS rather than immediately: pausing mid-fade freezes the outgoing clip
     * while it is still half visible, which reads as a stutter on every swap.
     */
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    fadeTimer.current = setTimeout(() => {
      fadeTimer.current = undefined;
      outgoing?.pause();
    }, FADE_MS);
    syncWatch();
    setStatus("ready");

    // Announce to any followers, from HERE rather than from `show`: `show` queues a clip
    // into the hidden layer, which may never be revealed if it fails to buffer. Publishing
    // there would put followers on a clip the leader never actually displayed.
    const bus = syncBusRef.current;
    if (bus) {
      bus.publish(region.id, {
        item: pend.item,
        // The leader's OWN resolved url, so a follower's differently-sized tile cannot
        // pick a different rendition and turn one download into two.
        src: rungUrl(pend.item.url, rungFor(pend.item)),
        startedAt: Date.now(),
        video: pend.into === "back" ? backVideo.current : frontVideo.current,
      });
    }
  }, [region.id, rungFor, registry, syncWatch]);

  /** Is `id` currently on screen in some OTHER region of this display? Lets a region
   *  skip a clip already showing elsewhere, so the same video never plays in two
   *  tiles at once — visual variety plus one fewer simultaneous fetch/decode.
   *  BOTH halves of a sibling's crossfade count: the clip it is visibly playing AND the
   *  one queued into its hidden layer — either one could otherwise be double-picked
   *  during the swap window. */
  const isVisibleElsewhere = useCallback(
    (id: string) => {
      for (const [rid, ctrl] of registry) {
        if (rid === region.id) continue;
        if (ctrl.current()?.id === id || ctrl.pending()?.id === id) return true;
      }
      return false;
    },
    [registry, region.id],
  );

  /**
   * From `fromIdx`, the next buffer index worth playing: not already showing in another
   * region, not RECENTLY shown anywhere on this display, and — when auto-optimise is
   * on — the best fit for this tile's orientation.
   *
   * Only scans the OTHER `buf.length - 1` slots, so it never returns `fromIdx` itself
   * (which would re-crossfade the current clip and double-count its view). If every other
   * buffered clip is visible elsewhere (tiny pool, many regions) it advances by one anyway.
   *
   * Both the shape preference and the recency memory are RULES over the buffer, not
   * filters applied to it, and that is load-bearing: a narrow keyword pool can be smaller
   * than the recency window (measured: one tag has THREE servable clips), and dropping
   * candidates outright would leave nothing to play. The scan is two-tier — fresh
   * candidates first, and when the whole pool is recent it falls back to exactly the
   * ranked walk it always did, so a tiny pool degrades to the old behaviour instead of
   * stalling.
   *
   * It also means the auto-optimise toggle takes effect on the next advance, with no
   * refetch and no reload, and that a region dragged from tall to wide re-prefers itself
   * as soon as the ResizeObserver reports the new box.
   */
  const nextDistinctIndex = useCallback(
    (fromIdx: number) => {
      const buf = itemsRef.current;
      const shape = fitRef.current.shape;
      const recent = recentOf(registry);
      const now = Date.now();
      const isRecent = (id: string) => {
        const at = recent.get(id);
        return at !== undefined && now - at < RECENT_MS;
      };
      let bestFresh: number | null = null;
      let bestFreshPenalty = Infinity;
      let bestAny: number | null = null;
      let bestAnyPenalty = Infinity;
      let idx = fromIdx;
      for (let tries = 0; tries < buf.length - 1; tries++) {
        idx = (idx + 1) % buf.length;
        const item = buf[idx];
        if (!item || isVisibleElsewhere(item.id)) continue;
        const penalty = aspectPenalty(item, shape);
        if (isRecent(item.id)) {
          if (penalty < bestAnyPenalty) {
            bestAny = idx;
            bestAnyPenalty = penalty;
          }
          continue;
        }
        // 0 is as good as it gets, and taking the FIRST fresh one keeps the walk in
        // buffer order — so a region works through its matching clips rather than
        // sticking on whichever one happens to sort best.
        if (penalty === 0) return idx;
        if (penalty < bestFreshPenalty) {
          bestFresh = idx;
          bestFreshPenalty = penalty;
        }
      }
      return bestFresh ?? bestAny ?? (fromIdx + 1) % buf.length; // all dupes — advance anyway
    },
    [isVisibleElsewhere, registry],
  );

  /**
   * Queue a buffer index into the HIDDEN layer without swapping yet. The current
   * clip keeps playing until the new element fires canplay / onload (see
   * renderSlot), at which point reveal() crossfades it in — so a swap never
   * flashes a black/poster frame. The timer is the safety net for a clip that
   * never buffers (broken source / dead-slow network).
   */
  const show = useCallback(
    (idx: number) => {
      const item = itemsRef.current[idx];
      if (!item) return;
      const k = slotSeq.current++;
      const into: "front" | "back" = frontIsTop.current ? "back" : "front";
      // Claimed BEFORE the fetch, so `arm` sees a swap in progress and holds its next
      // beat rather than stacking another cold clip onto a link already behind.
      pendingRevealRef.current = { key: k, item, into, mounted: false };
      // A new clip starts clean: whatever was stuck is on its way out.
      stuckRef.current = false;
      if (revealTimer.current) {
        clearTimeout(revealTimer.current);
        revealTimer.current = undefined;
      }

      /*
       * Get the bytes first, mount second — when something is already on screen.
       *
       * The obvious build — mount the element and warm the cache alongside it — races the
       * two, and the race is lost precisely when losing it hurts. Measured under a
       * 12 Mbit/s / 60 ms link: 102 of 265 media requests were repeats, because the warm
       * had not reached the cache before the element asked, so both fetched the same clip.
       * On a fast link the same code shows ~1 repeat per run, which is how the problem
       * hides during development.
       *
       * Waiting costs nothing visible THEN: the readiness gate holds the current clip on
       * screen until the incoming one can play, so a tile that waits is a tile still
       * playing — the wall degrades by switching less often, not by going black or by
       * stampeding a full link.
       *
       * On a BARE tile there is nothing to hold. Waiting leaves a black spinner until the
       * warm lands — Goonette of the Hill after Back / on round 2: the defending champion
       * (url already warmed) paints, the three challengers sit black for seconds. Mount
       * immediately there; the element fetches itself (no parallel warm — that is the
       * double-download above). A poster rides along so the tile is a still rather than
       * black while the first bytes arrive — same exception the stuck-reveal timeout makes.
       */
      const src = mediaSrc(item, rungFor(item));
      const bare = visibleItemRef.current == null;

      const mountSlot = () => {
        if (!mountedRef.current) return;
        const pend = pendingRevealRef.current;
        if (!pend || pend.key !== k) return; // superseded while we were fetching
        pend.mounted = true;
        if (into === "back") setBack({ key: k, item, visible: false, ...(bare ? { poster: true } : {}) });
        else setFront({ key: k, item, visible: false, ...(bare ? { poster: true } : {}) });

        if (bare) {
          // Still on screen now; canplay will start playback. No stuck-net: we already
          // revealed, and a late canplay must not re-arm as a still.
          reveal(k);
        } else {
          // The stuck-clip net starts HERE, not at `show`: it measures how long an element
          // takes to become playable, and until now there was no element.
          const fire = () => {
            // Held by the decode budget (no grant): it is paused ON PURPOSE and cannot
            // reach canplay — not a stuck clip. Wait for the grant rather than reveal a
            // poster and report a stall that is the budget's own doing.
            if (!grantRef.current && !pausedRef.current) {
              revealTimer.current = setTimeout(fire, REVEAL_TIMEOUT_MS);
              return;
            }
            // This clip never became playable and is about to be revealed regardless. Give
            // it its poster now — a still beats a black tile, and this is the one path
            // where a poster is looked at, so it is the one path that pays to fetch one.
            const withPoster = (s: Slot): Slot => (s.key === k ? { ...s, poster: true } : s);
            if (into === "back") setBack(withPoster);
            else setFront(withPoster);
            reveal(k);
            // ...and then MOVE ON. What is on screen now is a still that will fire no
            // `ended`; the advance timer armed for a playing video would hold it for ten
            // minutes. Re-arm as a still (see stuckRef), and say so — a wall that keeps
            // hitting this has a supply problem worth a number.
            stuckRef.current = true;
            track("media_stall", { rung: rungFor(item), reveal: "timeout", ...budgetRef.current.eventProps() });
            armRef.current();
          };
          revealTimer.current = setTimeout(fire, REVEAL_TIMEOUT_MS);
        }

        // Only now read ahead, and at the lower priority: this tile is served, so the
        // next clip must not compete with a tile that still has nothing to show.
        //
        // Through the same picker `goNext` will use, not `idx + 1`. It was an
        // approximation already — it ignored what other regions were showing — and the
        // shape preference widens the gap enough to matter: a portrait tile over a
        // landscape-heavy buffer would warm clip after clip it then skips, spending
        // bandwidth on exactly the frames this feature exists to stop fetching.
        const buf = itemsRef.current;
        const upcoming = buf.length > 1 ? buf[nextDistinctIndex(idx)] : undefined;
        if (upcoming) void warmMedia(mediaSrc(upcoming, rungFor(upcoming)), "next");
      };

      if (bare) mountSlot();
      else void warmMedia(src, "now").then(mountSlot);
    },
    [reveal, rungFor, nextDistinctIndex],
  );

  /**
   * On wrap, a FRESH page replaces the buffer. The playback adapters return no cursor,
   * so without this a region walked one shuffled page in the identical order for its
   * whole life — and for keyword sources the server now samples ORDER BY RANDOM(), so a
   * re-fetch changes the pool's MEMBERSHIP, not just its order (playlists get a new
   * permutation, which is still better than the same one forever). Failure reshuffles
   * in place: a wrap must never stall the walk.
   */
  const refreshBuffer = useCallback(async (): Promise<void> => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await api.fetchSource(region.source, undefined);
      if (!mountedRef.current) return;
      itemsRef.current = shuffle(res.items.length > 0 ? res.items : itemsRef.current);
    } catch {
      if (!mountedRef.current) return;
      itemsRef.current = shuffle(itemsRef.current);
    } finally {
      fetchingRef.current = false;
      // The walk restarts from the head of the new order; the on-screen clip is
      // tracked by visibleItemRef, not by this index, so nothing visible moves.
      indexRef.current = -1;
    }
  }, [region.source]);

  /** Move to the next (distinct) buffer item and schedule the following advance. */
  const goNext = useCallback(() => {
    const buf = itemsRef.current;
    if (buf.length === 0) return;
    const next = nextDistinctIndex(indexRef.current);
    const wrapped = buf.length > 1 && next <= indexRef.current;
    indexRef.current = next;
    show(next);
    if (wrapped) {
      // Passed the end of this page — swap in a fresh one behind the current clip.
      void refreshBuffer();
    } else if (hasMoreRef.current && buf.length - next <= PREFETCH_AHEAD) {
      // Top up the buffer when we get close to the tail (cursor-paging sources only).
      void fetchMore();
    }
  }, [show, fetchMore, nextDistinctIndex, refreshBuffer]);

  /**
   * (Re)arm the advance timer with a freshly rolled delay. When `stagger` is set
   * (the first arm after a load) the delay is a random fraction of the interval so
   * that regions sharing a timer don't all switch on the same beat.
   *
   * `since` re-times a photo hold that is ALREADY running (the Photo time slider moved):
   * the delay is what is left of the new hold measured from that start, and zero when
   * the photo has already been up longer — so it advances now, through the same beat
   * below (and its don't-stack-loads guard) as any other advance.
   */
  const arm = useCallback(
    (stagger = false, since?: number) => {
      // Any arm that is not resuming a hold starts a new one (a new clip, a skip).
      if (since === undefined) photoServedRef.current = null;
      clearAdvance();
      if (suspended()) return;
      if (itemsRef.current.length === 0) return;
      const cur = itemsRef.current[indexRef.current];
      // A video advances when it ENDS — and since its rendition is cut to the clip's
      // length bucket, that end is exactly the band the region was filtered to. This
      // timeout is only the stuck-clip net. A still has no end, so it gets a duration —
      // and so does a video that never became playable (stuckRef): revealed on its
      // poster, it IS a still, and waiting ten minutes for an `ended` that cannot come
      // was the freeze.
      let delay: number;
      if (cur?.kind === "video" && !stuckRef.current) {
        delay = FULL_VIDEO_SAFETY_MS;
      } else if (cur?.kind === "video") {
        // Stuck on its poster: recovery, not a photo — the fixed hold, whatever the
        // region's Photo time says.
        delay = FULL_IMAGE_SECONDS * 1000;
        if (stagger) delay *= 0.2 + Math.random() * 0.8;
      } else {
        const start = since ?? Date.now();
        delay = Math.max(0, photoMsRef.current - (Date.now() - start));
        // Stagger only stills: videos end when they end, so there is no shared beat to
        // scatter, and a wall of images would otherwise flip in lockstep.
        if (stagger) delay *= 0.2 + Math.random() * 0.8;
        photoSinceRef.current = start;
      }
      advanceTimer.current = setTimeout(() => {
        advanceTimer.current = undefined;
        photoSinceRef.current = null;
        if (suspended()) return;
        // Don't stack loads: if the last swap hasn't landed yet (slow network),
        // keep the current clip on screen and re-check next beat instead of
        // piling another cold fetch onto a saturated pipe.
        if (pendingRevealRef.current) {
          arm();
          return;
        }
        goNext();
        arm();
      }, delay);
    },
    [clearAdvance, goNext],
  );
  armRef.current = arm;

  // THE SLIDER MOVED. Re-time the photo on screen against the new hold rather than
  // waiting out the old one — a 30s photo dragged down to 2s should flip now, not in
  // 28 seconds. Only a running photo hold is touched: a video, a stuck clip and a paused
  // or suspended tile have no photo timer armed (photoSinceRef is null) and pick the new
  // value up the next time they arm one.
  const photoMs = regionPhotoSeconds(region) * 1000;
  useEffect(() => {
    if (photoMsRef.current === photoMs) return;
    photoMsRef.current = photoMs;
    const since = photoSinceRef.current;
    if (since !== null && advanceTimer.current) arm(false, since);
  }, [photoMs, arm]);

  /** Drop the stall watchdog — for one slot, or (no key) whichever is armed. */
  const clearStall = useCallback((key?: number) => {
    const s = stallTimer.current;
    if (!s || (key !== undefined && s.key !== key)) return;
    clearTimeout(s.t);
    stallTimer.current = undefined;
  }, []);

  /**
   * The visible video reported that it is waiting on data. Give it STALL_ADVANCE_MS to
   * resume — `playing`/`timeupdate` cancel this — and then move on without it.
   *
   * Only the slot on screen. The hidden layer buffering is the readiness gate's
   * business (it has its own timeout in `show`), and the outgoing layer of a crossfade
   * is paused on purpose. Skipped while a swap is already in flight: the incoming clip
   * will take over, and superseding it would restart its warm from zero.
   */
  const armStall = useCallback(
    (key: number) => {
      if (key !== visibleKeyRef.current || stallTimer.current?.key === key) return;
      clearStall();
      stallTimer.current = {
        key,
        t: setTimeout(() => {
          stallTimer.current = undefined;
          if (key !== visibleKeyRef.current || suspended() || pendingRevealRef.current) return;
          const item = visibleItemRef.current;
          track("media_stall", { rung: item ? rungFor(item) : null, reveal: "playing", ...budgetRef.current.eventProps() });
          goNext();
          arm();
        }, STALL_ADVANCE_MS),
      };
    },
    [clearStall, suspended, goNext, arm, rungFor],
  );

  /**
   * Start the layers that should be running, and only those: the one on screen, plus
   * one waiting to be revealed (it has to reach `canplay` to get there).
   *
   * Deliberately NOT "both layers". The faded-out layer is paused on purpose once a
   * crossfade lands (see `reveal`), and a resume that woke it would hand back the
   * doubled decode load that pausing it exists to remove.
   */
  const playLive = useCallback(() => {
    const visible = frontIsTop.current ? frontVideo.current : backVideo.current;
    void visible?.play().catch(() => undefined);
    const pend = pendingRevealRef.current;
    // `mounted` guards the window where a swap has been claimed but its clip is still
    // being fetched: the layer's ref still points at the OLD element there, and waking
    // that would restore the doubled decode that pausing it exists to remove.
    if (!pend?.mounted) return;
    const incoming = pend.into === "back" ? backVideo.current : frontVideo.current;
    if (incoming && incoming !== visible) void incoming.play().catch(() => undefined);
  }, []);

  // --- Imperative controller exposed to the DisplayApp command bus. ---
  useEffect(() => {
    const ctrl: RegionController = {
      pause: () => {
        pausedRef.current = true;
        budgetRef.current.poke();
        clearAdvance();
        frontVideo.current?.pause();
        backVideo.current?.pause();
      },
      resume: () => {
        pausedRef.current = false;
        budgetRef.current.poke();
        playLive();
        arm();
      },
      // Refused while a swap is in flight — superseding the pending slot would restart
      // its warm from zero, and a held Space would stack cold fetches onto a saturated
      // pipe. Same rule as the timer's own beat above; the first press starts the skip,
      // repeats while the incoming clip buffers are no-ops.
      advance: () => {
        if (itemsRef.current.length === 0 || pendingRevealRef.current) return;
        goNext();
        arm();
      },
      // What is VISIBLE, not what indexRef names: the index advances the moment a swap
      // starts, and reporting the incoming clip here is what let sibling regions
      // re-pick the one still on screen.
      current: () => visibleItemRef.current,
      pending: () => pendingRevealRef.current?.item ?? null,
      // Reads the VISIBLE item for the same reason `current` does: mid-swap the tile is
      // still playing the outgoing clip, and that is the one making noise.
      audible: () => {
        const item = visibleItemRef.current;
        return item?.kind === "video" && item.audible !== false;
      },
      // The decode budget's view (playbackBudget.ts).
      video: () => (frontIsTop.current ? frontVideo.current : backVideo.current),
      wantsToPlay: () => !pausedRef.current && onscreenRef.current,
      onGlass: () => onscreenRef.current,
      // No frame of its OWN on screen: nothing visible yet, or a video that has not
      // painted one (readyState < 2) and has no parked snapshot to show instead. A poster
      // counts as bare — it is a stand-in, which is what the first-frame pass is for.
      bare: () => {
        const it = visibleItemRef.current;
        if (!it || stuckRef.current) return true;
        if (it.kind !== "video") return false;
        const v = frontIsTop.current ? frontVideo.current : backVideo.current;
        return !v || (v.readyState < 2 && !(v.poster || "").startsWith("data:"));
      },
      rung: () => (visibleItemRef.current ? rungFor(visibleItemRef.current) : null),
      area: () => (containerRef.current ? containerRef.current.clientWidth * containerRef.current.clientHeight : 0),
    };
    registry.set(region.id, ctrl);
    return () => {
      registry.delete(region.id);
    };
  }, [region.id, registry, arm, clearAdvance, goNext, playLive, onscreenRef, rungFor]);

  // This tile's grant. Subscribing also starts the display's monitor (first tile).
  useEffect(
    () =>
      budget.subscribe(region.id, (g) => {
        grantRef.current = g;
        setGranted(g);
      }),
    [budget, region.id],
  );

  // Off the glass, paused or held: the watch interval stops, and comes back with the tile.
  useEffect(() => {
    syncWatch();
  }, [paused, onscreen, granted, syncWatch]);

  // --- React to global pause/resume state changes. ---
  useEffect(() => {
    if (!granted) {
      // Held by the decode budget: hold the picture, give the decoder back (decoderPark).
      if (photoSinceRef.current !== null) photoServedRef.current = Date.now() - photoSinceRef.current;
      clearAdvance();
      park(frontVideo.current);
      park(backVideo.current);
      return;
    }
    // Granted again: the LIVE layers take their sources back — the one on screen, and one
    // waiting to be revealed. A faded-out layer stays parked; it is only ever replaced.
    unpark(frontIsTop.current ? frontVideo.current : backVideo.current, false);
    const pend = pendingRevealRef.current;
    if (pend?.mounted) unpark(pend.into === "back" ? backVideo.current : frontVideo.current, false);
    if (paused || !onscreen) {
      clearAdvance();
      frontVideo.current?.pause();
      backVideo.current?.pause();
    } else {
      playLive();
      // A photo that lost its turn mid-hold resumes it (see photoServedRef).
      const served = photoServedRef.current;
      arm(false, served === null ? undefined : Date.now() - served);
    }
    // `onscreen` alongside `paused`: a tile that goes off screen stops decoding and
    // stops advancing, and coming back resumes it — unless the wall is also paused,
    // which `playLive`/`arm` already honour through `suspended()`.
  }, [paused, onscreen, granted, arm, clearAdvance, playLive]);

  // Who wants to play changed: let the budget re-deal its slots (a no-op without a limit).
  useEffect(() => {
    budget.poke();
  }, [budget, paused, onscreen]);

  // --- (Re)load when the source changes; also the initial mount load. ---
  const srcKey = sourceKey(region);
  useEffect(() => {
    mountedRef.current = true;
    // Reset the buffer for the new source.
    itemsRef.current = [];
    indexRef.current = 0;
    cursorRef.current = undefined;
    hasMoreRef.current = true;
    fetchingRef.current = false;
    visibleItemRef.current = null;
    setStatus("loading");
    setFront({ key: slotSeq.current++, item: null, visible: true });
    setBack({ key: slotSeq.current++, item: null, visible: false });
    frontIsTop.current = true;
    pendingRevealRef.current = null;
    if (revealTimer.current) clearTimeout(revealTimer.current);

    let cancelled = false;
    void (async () => {
      await fetchMore();
      if (cancelled || !mountedRef.current) return;
      if (itemsRef.current.length > 0) {
        // Start on a clip not already showing in another region (best-effort — other
        // regions may still be loading; goNext keeps resolving dupes as they advance).
        const start = nextDistinctIndex(itemsRef.current.length - 1);
        indexRef.current = start;
        show(start);
        arm(true); // stagger the first switch so screens desync
      }
    })();

    return () => {
      cancelled = true;
      clearAdvance();
      clearStall();
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      if (revealTimer.current) clearTimeout(revealTimer.current);
    };
    // srcKey captures source identity; the callbacks are stable per source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcKey]);

  // --- Final unmount cleanup. ---
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearAdvance();
      clearStall();
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      if (revealTimer.current) clearTimeout(revealTimer.current);
    };
  }, [clearAdvance, clearStall]);

  // --- Auto-retry when in an error/empty state with nothing buffered. ---
  useEffect(() => {
    if (status !== "error") return;
    retryTimer.current = setTimeout(() => {
      hasMoreRef.current = true;
      cursorRef.current = undefined;
      setStatus("loading");
      void (async () => {
        await fetchMore();
        if (!mountedRef.current) return;
        if (itemsRef.current.length > 0) {
          indexRef.current = 0;
          show(0);
          arm();
        } else {
          setStatus((s) => (s === "loading" ? "error" : s));
        }
      })();
    }, retryDelayRef.current);
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [status, fetchMore, show, arm]);

  // Adaptive Framing drives the two video elements directly — no state, no re-render.
  // Both slots are covered because the outgoing one is still visible through the
  // crossfade, and a tile that snapped back to centre-cover for 600ms would read as a
  // lurch rather than a dissolve.
  useAdaptiveFraming(
    [
      { ref: frontVideo, item: front.item, epoch: front.key },
      { ref: backVideo, item: back.item, epoch: back.key },
    ],
    region,
    box,
    FRAMING_MOVE_MS,
  );

  const renderSlot = (
    slot: Slot,
    ref: React.MutableRefObject<HTMLVideoElement | null>,
    z: number,
  ) => {
    if (!slot.item) return null;
    const it = slot.item;
    // Once per slot render: the element's src, the failure report and the latch decision
    // all have to be talking about the same rung.
    const rung = rungFor(it);
    const common =
      "absolute inset-0 h-full w-full transition-opacity ease-in-out " + fit;
    return (
      <div
        key={slot.key}
        className="absolute inset-0"
        style={{
          zIndex: z,
          opacity: slot.visible ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
        }}
      >
        {it.kind === "video" ? (
          <video
            // Callback rather than the bare ref object: `volume` has no attribute form,
            // so the mount is the one chance to seed it before the element is audible —
            // the [volume] effect only reaches elements that already exist.
            ref={(v) => {
              ref.current = v;
              if (!v) return;
              v.volume = volumeRef.current;
              /*
               * An element that mounts while this tile is SUSPENDED — off screen, or the
               * wall paused — must not autoplay. The [paused, onscreen] effect pauses the
               * elements that exist when the state changes, but `autoPlay` starts a fresh
               * element on its own, so a tile that scrolled away before its clip arrived
               * would decode unseen from the moment it landed (Infinity Walls mounts its
               * neighbour pages exactly like that, for the read-ahead). A pause() before
               * any data has arrived clears the element's autoplay flag; playLive() starts
               * it the moment the tile is back.
               */
              if (suspended()) v.pause();
              // Held by the decode budget from birth: do not even take a decoder (see
              // decoderPark). Unparked with its grant, by the effect below.
              if (!grantRef.current) park(v);
            }}
            className={common}
            src={rungUrl(it.url, rung)}
            // Bare first paint or failed-to-buffer — see Slot.poster.
            poster={slot.poster ? it.posterUrl : undefined}
            autoPlay
            playsInline
            // Never loop. A clip's end IS the advance, and a looping element never
            // fires `ended` — so looping here would strand the tile on one clip.
            loop={false}
            muted={muted}
            controls={false}
            crossOrigin={corsModeFor(it.url)}
            // Picture-in-Picture composites decoded frames on an OS surface that CSS
            // filters never reach, so it would pop a safe-mode clip out fully clear.
            disablePictureInPicture={isSafeMode()}
            onCanPlay={(e) => {
              const v = e.currentTarget;
              if (suspended()) {
                // Suspended — off screen, or the wall paused. `autoPlay` starts this
                // element on its own the moment it has data, and the pause at mount
                // (see the ref above) is not always enough to call that off; here the
                // element demonstrably HAS data, so a pause lands whether autoplay has
                // begun or is about to. playLive() starts it when the tile is back.
                v.pause();
              } else {
                v.play().catch(() => {
                  // Autoplay blocked while unmuted — fall back to muted playback, and say
                  // so, so the screen's button does not claim sound that is not playing.
                  if (!v.muted) {
                    v.muted = true;
                    v.play().catch(() => undefined);
                    onAutoMutedRef.current?.();
                  }
                });
              }
              /*
               * Correct the sync bus's element once it exists. `reveal` announces from the
               * `bare` path (a source that just changed — Loop pins a clip, Stop looping
               * releases it) BEFORE this element has mounted, so it published a null/stale
               * `video`. A follower given no element has nothing to capture and was left
               * frozen on its last frame. This is the first moment the real element is
               * known; republishing the same state with it re-points every follower.
               */
              const bus = syncBusRef.current;
              if (bus && slot.key === visibleKeyRef.current) {
                const cur = bus.get(region.id);
                if (cur && cur.video !== v) bus.publish(region.id, { ...cur, video: v });
              }
              // Readiness gate: this slot can play now, so crossfade it in.
              if (pendingRevealRef.current?.key === slot.key) reveal(slot.key);
              // A clip the reveal timeout had given up on came good after all: it is
              // playing now, so hand it back the playing video's timer — `ended` will
              // advance it — rather than cutting it off on the still's six seconds.
              if (stuckRef.current && slot.key === visibleKeyRef.current) {
                stuckRef.current = false;
                arm();
              }
            }}
            // The stall watchdog, for the slot on screen only (armStall checks).
            onWaiting={() => armStall(slot.key)}
            onStalled={() => armStall(slot.key)}
            onPlaying={() => clearStall(slot.key)}
            onTimeUpdate={(e) => {
              clearStall(slot.key);
              // The clip's first view, once it has really played on the glass (syncWatch).
              if (
                slot.key === visibleKeyRef.current &&
                countedKeyRef.current !== slot.key &&
                e.currentTarget.currentTime >= VIEW_MIN_PLAY_SEC &&
                !suspended()
              ) {
                countedKeyRef.current = slot.key;
                onViewRef.current?.(it);
              }
            }}
            onEnded={(e) => {
              // Guard so only the *visible* clip advances (the outgoing clip in a
              // crossfade must not double-advance) and never while paused.
              if (slot.key !== visibleKeyRef.current || suspended()) return;
              // A clip played out is a turn over: when tiles are taking turns to decode,
              // this one goes to the back of the line. After this handler, so the advance
              // below is queued first and simply waits for the next grant.
              setTimeout(() => budgetRef.current.yield(region.id), 0);
              // Bridge the gap: replay from the top until the incoming clip can take
              // over — a region never sits on a frozen last frame while its successor
              // buffers. `loop` stays false because this event IS the advance trigger;
              // the replay is explicit. And an `ended` that fires while a swap is
              // already in flight bridges WITHOUT calling goNext — superseding the
              // pending slot would restart its warm from zero.
              const v = e.currentTarget;
              v.currentTime = 0;
              v.play().catch(() => undefined);
              // A playthrough finished and the clip starts again: that is another view.
              // `reveal` counts the FIRST play, this counts every one after it, so a clip
              // pinned with the loop button accrues one per repeat instead of one forever.
              //
              // It also fires when the replay is only bridging a slow successor, and that
              // is counted too — the clip genuinely started playing again on a screen,
              // which is the definition the count is being held to. The guard above means
              // only the visible slot ever reaches here, so a crossfade's outgoing clip
              // cannot double-count. A clip shorter than VIEW_MIN_PLAY_SEC gets its first
              // view here too, hence the mark.
              countedKeyRef.current = slot.key;
              onViewRef.current?.(it);
              if (pendingRevealRef.current) return;
              /*
               * A ONE-CLIP SOURCE LOOPS IN PLACE. The replay above already restarted the
               * element; advancing a one-item buffer would only `show` the same clip into
               * the other slot and crossfade to it — a fresh element, a fresh fetch (from
               * cache, but a fetch), and a fade between two copies of one picture on every
               * loop. A locked clip on a screen and every region of Infinity Walls are
               * exactly this case. A synced follower is unaffected: it re-captures on the
               * leader element's own readiness events, which the replay fires.
               *
               * "Every buffered item is this clip" rather than "the buffer holds one":
               * StrictMode's double mount lands the first page twice (fetchMore appends,
               * and the cancelled run still appends), and a buffer of two copies must
               * loop the same way one copy does.
               */
              if (itemsRef.current.every((x) => x.id === it.id)) {
                arm();
                return;
              }
              goNext();
              arm();
            }}
            onError={(e) => {
              // A parked element (decoderPark) has no source on purpose; not a failure.
              if (isParked(e.currentTarget)) return;
              // Rendition failed to load → drop this region to the full master. `rung`
              // is already null once the latch has fired (and when auto-optimise is off),
              // so this is only ever the FIRST failure — which is the one worth reporting.
              const latching = rung != null;
              if (latching) setRenditionsOk(false);
              /*
               * A latching failure gets a second chance for free: the latch re-renders
               * this slot with the master's url and the fresh element fires `canplay`
               * like any other. When there is NO rung to fall back from — the master
               * itself failed — nothing else will ever fire on this element, and the
               * tile would sit to REVEAL_TIMEOUT_MS, be revealed black, and then wait
               * on `ended`. The <img> branch below already moves on in exactly this
               * case; a video that cannot load is no different. Suspended tiles do not
               * advance (nothing does while suspended), but are marked stuck so the
               * resume's `arm` gives them a still's six seconds, not a video's ten
               * minutes.
               */
              if (!latching) {
                const pending = pendingRevealRef.current?.key === slot.key;
                if (pending || slot.key === visibleKeyRef.current) {
                  stuckRef.current = true;
                  if (!suspended()) {
                    if (pending) reveal(slot.key);
                    goNext();
                    arm();
                  }
                }
              }
              /*
               * The latch is why this is reported at all: it is sticky for the life of
               * the region, so ONE failure costs this tile every rendition until it
               * remounts, and the only visible symptom is that the wall quietly gets
               * heavier. Nobody files a bug for that.
               *
               * `code` alone does NOT distinguish the cases that look identical from the
               * outside — 4 (SRC_NOT_SUPPORTED) is returned both for a codec the browser
               * refuses and for a response it was blocked from reading, e.g. a 403 once
               * the age token or url signature has lapsed — so `http_status` is probed
               * from the same url (see probeMediaStatus).
               *
               * Rate-limited to 5/min in track(): a lapsed token fails every tile on
               * every advance, which is ~1,150 an hour from a single browser. The probe
               * is gated on the same limit, so it never runs for an event that would be
               * dropped anyway. `code` and the url are read NOW: the event's target is
               * gone by the time the probe resolves.
               */
              const video = e.currentTarget as HTMLVideoElement;
              const code = video.error?.code ?? null;
              // The browser's own reason, which `code` flattens: Chrome's reads e.g.
              // "DEMUXER_ERROR_COULD_NOT_OPEN" for bytes that are not a video at all —
              // which a 200/206 probe cannot tell from a good file. Issue #11 was JPEGs
              // catalogued as videos, reported only as "code 4, 206" until this said so.
              const message = mediaErrorMessage(video.error);
              // A decoder that would not start, or broke: the decode budget's second signal
              // (a failed decoder yields no frames for its dropped-frame monitor to count).
              // Before the rate limit below — the budget needs every one, analytics does not.
              if (code === 3 || /decod/i.test(message ?? "")) budgetRef.current.decodeFailure();
              if (!wouldSend("media_error")) return;
              void probeMediaStatus(video.currentSrc || it.url).then((http_status) =>
                track("media_error", { code, rung, latched: latching, http_status, message, ...budgetRef.current.eventProps() }),
              );
            }}
          />
        ) : (
          <img
            className={common}
            src={it.url}
            alt=""
            draggable={false}
            onLoad={() => {
              if (pendingRevealRef.current?.key === slot.key) reveal(slot.key);
            }}
            /*
             * A still that cannot load must not become a permanently blank tile.
             *
             * The video path has a whole error branch; this had none, so a broken image
             * simply never fired its readiness gate — it hung to REVEAL_TIMEOUT_MS, got
             * revealed anyway, and then sat there, because `arm()` keeps ticking on a
             * tile whose content never arrived. Invisible in every metric, too: the
             * media_error the video path reports was never sent for a still.
             *
             * Uploads make this reachable in a way it was not before, since an image on
             * the wall is now something a stranger supplies. Report it and move on: the
             * region advances to the next item rather than showing nothing.
             */
            onError={() => {
              track("media_error", { kind: "image", ...budgetRef.current.eventProps() });
              if (pendingRevealRef.current?.key === slot.key) reveal(slot.key);
              goNext();
              arm();
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden bg-[var(--color-ink)]"
      // The verdict, on the DOM: what a probe in a browser check reads to prove an
      // off-screen tile is not decoding, without reaching into React.
      data-onscreen={onscreen ? "" : undefined}
    >
      {renderSlot(front, frontVideo, frontIsTop.current ? 2 : 1)}
      {renderSlot(back, backVideo, frontIsTop.current ? 1 : 2)}

      {/* No hidden preloader element here on purpose — `show` warms the next clip through
          `warmMedia`, which explains why. */}

      {status === "loading" && (
        <Placeholder>
          <Spinner />
        </Placeholder>
      )}
      {status === "empty" && (
        <Placeholder>
          <span className="text-xs tracking-wide text-zinc-500">No media</span>
        </Placeholder>
      )}
      {status === "error" && (
        <Placeholder>
          <span className="text-xs tracking-wide text-zinc-500">
            Source unavailable — retrying…
          </span>
        </Placeholder>
      )}
    </div>
  );
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--color-panel)]/30">
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div
      className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]"
      aria-label="loading"
    />
  );
}
